import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Factory, Shield, TrendingUp, Users, CheckCircle, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const ManufacturingIndustry = () => {
  const features = [
    { icon: Users, title: 'Shift Rotation Management', description: 'Optimize 24/7 production schedules across multiple shifts' },
    { icon: Shield, title: 'Certification Tracking', description: 'Ensure workers have required safety and equipment training' },
    { icon: Factory, title: 'Equipment Training', description: 'Match operators to machines based on certifications' },
    { icon: TrendingUp, title: 'Production Optimization', description: 'Align staffing with production targets and KPIs' }
  ];

  const benefits = [
    '38% increase in productivity',
    '29% reduction in downtime',
    '96% compliance rate',
    'Real-time shift visibility',
    'Automated safety compliance',
    'Skills-based scheduling'
  ];

  const stats = [
    { value: '250+', label: 'Manufacturing Plants' },
    { value: '38%', label: 'Productivity Increase' },
    { value: '96%', label: 'Compliance Rate' },
    { value: '29%', label: 'Downtime Reduction' }
  ];

  return (
    <>
      <LandingNav />
      
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-700 via-gray-700 to-zinc-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center space-x-2 mb-4">
                <Factory className="w-8 h-8" />
                <span className="text-gray-300 font-semibold">Manufacturing & Production</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Maximize Production Efficiency with Smart Workforce Planning
              </h1>
              <p className="text-xl text-gray-200 mb-8">
                Streamline shift rotations, maintain safety compliance, and optimize your production line staffing with intelligent scheduling.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-slate-700 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center">
                  Schedule a Demo
                </Link>
                <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center">
                  View Pricing
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <img src="/Timesheet Page.png" alt="Manufacturing Dashboard" className="rounded-2xl shadow-2xl border-4 border-white/20" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-slate-700 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Engineered for Manufacturing Excellence</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Keep production lines running smoothly with optimized workforce management</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}>
              <img src="/Analyticspage.png" alt="Production Analytics" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Proven Impact on Production</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-slate-700 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            "Shiftry's certification tracking and shift rotation features have been game-changers for our production efficiency and safety compliance."
          </blockquote>
          <p className="text-lg text-gray-600">- David Thompson, Plant Manager, PrecisionTech Manufacturing</p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-slate-700 to-zinc-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Optimize Your Manufacturing Operations?</h2>
          <p className="text-xl text-gray-200 mb-8">Join 250+ manufacturing plants increasing productivity and compliance</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-slate-700 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Schedule Your Free Demo
            </Link>
            <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default ManufacturingIndustry;
